import axios from 'axios';

const api = axios.create({
  baseURL: '/api/',
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  sessionStorage.clear();
  // Diferido a la siguiente vuelta del event loop: si esto corre en medio de
  // un commit de React (p. ej. un 401 durante el envío de un formulario),
  // la navegación dura de window.location.href puede empezar a desmontar el
  // DOM mientras React todavía está insertando nodos, y tira
  // "insertBefore: node is not a child of this node". Dejar que el commit
  // en curso termine antes de navegar evita la carrera.
  setTimeout(() => {
    window.location.href = '/';
  }, 0);
};

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Cache-Control'] = 'no-cache, no-store';
  config.headers['Pragma'] = 'no-cache';
  return config;
});

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = sessionStorage.getItem('refresh_token');
      if (!refreshToken) {
        processQueue(error);
        isRefreshing = false;
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      try {
        const res = await axios.post('/api/token/refresh/', { refresh: refreshToken });
        const newToken = res.data.access;
        sessionStorage.setItem('token', newToken);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Retry transient failures (nginx/proxy errors) on GET requests, up to 2 times
    const method = (originalRequest.method || '').toUpperCase();
    const retryCount = originalRequest._retryCount || 0;
    const isTransient = !status || status === 400 || status >= 500;
    const isLoginEndpoint = originalRequest.url?.includes('token/');

    if (method === 'GET' && isTransient && !isLoginEndpoint && retryCount < 2) {
      originalRequest._retryCount = retryCount + 1;
      await delay(1000 * originalRequest._retryCount);
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default api;
